import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuthStore } from '../stores/auth.store';
import { NavBar } from '../components/NavBar';

interface Status {
  hasOpenAI: boolean;
  hasHeyGen: boolean;
  hasBrave: boolean;
  topicsCount: number;
  scriptsCount: number;
  scriptsApproved: number;
  videosCount: number;
  videosApproved: number;
  publicationsCount: number;
}

export default function Onboarding() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [s, setS] = useState<Status | null>(null);

  useEffect(() => {
    if (!user) { navigate('/login', { replace: true }); return; }
    Promise.all([
      api.get('/settings').catch(() => ({ data: { apiKeys: [] } })),
      api.get('/topics').catch(() => ({ data: { topics: [] } })),
      api.get('/scripts').catch(() => ({ data: { scripts: [] } })),
      api.get('/videos').catch(() => ({ data: { videos: [] } })),
      api.get('/publications').catch(() => ({ data: { publications: [] } })),
    ]).then(([k, t, sc, v, p]) => {
      const keys = (k.data.apiKeys ?? []) as Array<{ service: string; isValid: boolean }>;
      const hasKey = (n: string) => !!keys.find((x) => x.service === n && x.isValid);
      setS({
        hasOpenAI: hasKey('openai'),
        hasHeyGen: hasKey('heygen'),
        hasBrave:  hasKey('brave_search'),
        topicsCount:      (t.data.topics ?? []).length,
        scriptsCount:     (sc.data.scripts ?? []).length,
        scriptsApproved:  (sc.data.scripts ?? []).filter((x: { isApproved: boolean }) => x.isApproved).length,
        videosCount:      (v.data.videos ?? []).length,
        videosApproved:   (v.data.videos ?? []).filter((x: { isApproved: boolean }) => x.isApproved).length,
        publicationsCount:(p.data.publications ?? []).length,
      });
    });
  }, [user, navigate]);

  if (!s) {
    return <div className="min-h-screen lg:pl-64"><NavBar /><div className="p-8 text-slate-500">Загрузка…</div></div>;
  }

  const steps = [
    {
      n: 1, title: 'Подключи OpenAI ключ',
      done: s.hasOpenAI,
      hint: 'Нужен для генерации скриптов и эмбеддингов базы знаний. Получить — на platform.openai.com.',
      cta: 'Открыть Настройки', to: '/settings',
    },
    {
      n: 2, title: 'Подключи HeyGen ключ',
      done: s.hasHeyGen,
      hint: 'Нужен для создания видео с AI-аватарами. Получить — app.heygen.com → Account → API.',
      cta: 'Открыть Настройки', to: '/settings',
      optional: false,
    },
    {
      n: 3, title: 'Brave Search ключ',
      done: s.hasBrave,
      hint: 'Опционально. Нужен для веб-поиска в базе знаний и поиска хуков конкурентов. Free tier 2000/мес.',
      cta: 'Открыть Настройки', to: '/settings',
      optional: true,
    },
    {
      n: 4, title: 'Загрузи материалы (опционально)',
      done: false,
      hint: 'PDF/DOCX/URL — AI будет ссылаться на факты из них. Без этого скрипты будут общими.',
      cta: 'База знаний →', to: '/knowledge',
      optional: true,
    },
    {
      n: 5, title: 'Создай первую тему',
      done: s.topicsCount > 0,
      hint: 'Что хочешь снять? Название + платформы + язык. Можно загрузить пачкой.',
      cta: 'К темам', to: '/topics',
    },
    {
      n: 6, title: 'Сгенерируй скрипты',
      done: s.scriptsCount > 0,
      hint: 'Кнопка «✨ Сгенерировать скрипты» на странице Темы. AI напишет за 15-30 сек на тему.',
      cta: 'К темам', to: '/topics',
    },
    {
      n: 7, title: 'Одобри скрипты',
      done: s.scriptsApproved > 0,
      hint: 'Прочитай, отредактируй, нажми «✓ Одобрить». После — кнопка «🎬 Создать видео».',
      cta: 'К скриптам', to: '/scripts',
    },
    {
      n: 8, title: 'Сгенерируй и одобри видео',
      done: s.videosApproved > 0,
      hint: 'HeyGen занимает 5-10 минут. После готовности — «✓ Одобрить» → «📅 Запланировать».',
      cta: 'К видео', to: '/videos',
    },
    {
      n: 9, title: 'Запланируй публикацию',
      done: s.publicationsCount > 0,
      hint: 'Выбери дату, платформы и время. Система запостит автоматически.',
      cta: 'Календарь', to: '/calendar',
    },
  ] as const;

  const doneCount = steps.filter((x) => x.done).length;
  const progress = Math.round((doneCount / steps.length) * 100);

  return (
    <div className="min-h-screen lg:pl-64">
      <NavBar />
      <div className="max-w-3xl mx-auto px-4 md:px-8 pt-6 pb-16">
        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Начало работы 🎓</h1>
          <p className="text-slate-500 mt-2">
            9 шагов до первого опубликованного видео. Прогресс сохраняется автоматически.
          </p>
        </header>

        {/* Progress bar */}
        <div className="card-glass p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-2xl font-bold tracking-tight">{doneCount} <span className="text-slate-400 text-base font-normal">/ {steps.length} шагов</span></div>
              <div className="text-xs text-slate-500 mt-1">{progress === 100 ? '🎉 Всё готово, ты знаешь систему!' : `Осталось ${steps.length - doneCount}`}</div>
            </div>
            <div className="text-3xl font-bold bg-gradient-to-br from-indigo-500 to-violet-600 bg-clip-text text-transparent tabular-nums">
              {progress}%
            </div>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-indigo-500 to-violet-600 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Steps */}
        <ol className="space-y-3">
          {steps.map((step) => (
            <li
              key={step.n}
              className={`card-glass p-5 flex items-start gap-4 ${
                step.done ? 'opacity-70' : 'card-glass-hover'
              }`}
            >
              <div
                className={`w-10 h-10 rounded-2xl flex items-center justify-center text-white font-bold shrink-0 ${
                  step.done
                    ? 'bg-gradient-to-br from-emerald-500 to-green-600 shadow-[0_4px_14px_rgba(5,150,105,0.35)]'
                    : 'bg-gradient-to-br from-indigo-500 to-violet-600 shadow-[0_4px_14px_rgba(79,70,229,0.35)]'
                }`}
              >
                {step.done ? '✓' : step.n}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className={`font-semibold ${step.done ? 'line-through decoration-slate-300' : ''}`}>
                    {step.title}
                  </h3>
                  {'optional' in step && step.optional && (
                    <span className="badge badge-slate">опционально</span>
                  )}
                </div>
                <p className="text-sm text-slate-600 mt-1">{step.hint}</p>
                {!step.done && (
                  <Link to={step.to} className="btn btn-sm btn-primary mt-3">
                    {step.cta} →
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ol>

        <div className="card-glass p-6 mt-8 text-center">
          <div className="text-3xl mb-2">📖</div>
          <h3 className="font-semibold mb-1">Хочешь подробнее?</h3>
          <p className="text-sm text-slate-500 mb-4">
            В разделе «Руководство» — детальное описание каждого инструмента, FAQ и советы.
          </p>
          <Link to="/guide" className="btn btn-md btn-ghost">📖 Открыть руководство</Link>
        </div>
      </div>
    </div>
  );
}
